import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalShipping } from '@mui/icons-material';
import api from '../../services/api';
import type { PendingDelivery } from '../../types';
import { formatDay, formatMoney } from './contact';
import { todayIso } from '../expenses/ExpenseDialog';

/** Confirmación de entrega con la fecha (ahí se descuenta el stock). */
export const DeliverDialog: React.FC<{ order: PendingDelivery | null; onClose: () => void; onDone: () => void }> = ({ order, onClose, onDone }) => {
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (order) { setDate(todayIso()); setError(null); } }, [order]);

  const confirm = async () => {
    if (!order) return;
    setSaving(true);
    setError(null);
    try {
      await api.deliverOrder(order.id, date);
      onClose();
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo marcar como entregado.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!order} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Marcar entregado</DialogTitle>
      <DialogContent>
        {order && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {order.customerName} · {formatMoney(order.total)}<br />
              {order.items.map(i => `${i.quantity} × ${i.productName}`).join(', ')}
            </Typography>
            <TextField label="Fecha de entrega" type="date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Typography variant="caption" color="text.secondary">Al confirmar se descuenta el stock.</Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="success" onClick={confirm} disabled={saving} sx={{ minHeight: 44 }}>{saving ? 'Guardando…' : 'Confirmar entrega'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const usePendingDeliveries = () => {
  const [rows, setRows] = useState<PendingDelivery[] | null>(null);
  const load = useCallback(async () => {
    try { setRows(await api.getPendingDeliveries()); } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { rows, load };
};

/** Tabla de pedidos a entregar (PC). */
export const PendingDeliveriesList: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { rows, load } = usePendingDeliveries();
  const [delivering, setDelivering] = useState<PendingDelivery | null>(null);

  if (rows == null) return <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No hay pedidos pendientes de entrega.</Typography>;

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cliente</TableCell>
            <TableCell>Productos</TableCell>
            <TableCell>Pedido</TableCell>
            <TableCell>Entrega est.</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const late = r.estimatedDelivery && r.estimatedDelivery.slice(0, 10) < todayIso();
            return (
              <TableRow key={r.id} hover>
                <TableCell>{r.customerName || '—'}<Typography variant="caption" color="text.secondary" display="block">{r.reference}</Typography></TableCell>
                <TableCell>{r.items.map(i => `${i.quantity} × ${i.productName}`).join(', ')}{r.displaysDelivered > 0 ? ` + ${r.displaysDelivered} exhib.` : ''}</TableCell>
                <TableCell>{formatDay(r.createdAt)}</TableCell>
                <TableCell sx={{ color: late ? 'error.main' : undefined }}>{formatDay(r.estimatedDelivery)}</TableCell>
                <TableCell align="right">{formatMoney(r.total)}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="contained" color="success" onClick={() => setDelivering(r)}>Marcar entregado</Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <DeliverDialog order={delivering} onClose={() => setDelivering(null)} onDone={() => { load(); onChanged?.(); }} />
    </>
  );
};

/** Tarjetas de pedidos a entregar con botón grande (pantalla Hoy y modo celular). */
export const PendingDeliveryCards: React.FC<{ title?: (count: number) => React.ReactNode; hideWhenEmpty?: boolean }> = ({ title, hideWhenEmpty }) => {
  const { rows, load } = usePendingDeliveries();
  const [delivering, setDelivering] = useState<PendingDelivery | null>(null);
  if (rows == null) return hideWhenEmpty ? null : <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
  if (rows.length === 0) return hideWhenEmpty ? null : <Typography color="text.secondary">No hay pedidos pendientes de entrega.</Typography>;
  return (
    <>
      {title?.(rows.length)}
      <Stack spacing={1.5}>
        {rows.map(r => {
          const late = r.estimatedDelivery && r.estimatedDelivery.slice(0, 10) < todayIso();
          return (
            <Card key={r.id} variant="outlined" sx={{ borderLeft: 6, borderLeftColor: late ? 'error.main' : '#16a34a' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{r.customerName || '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {r.items.map(i => `${i.quantity} × ${i.productName}`).join(', ')}{r.displaysDelivered > 0 ? ` + ${r.displaysDelivered} exhib.` : ''}
                    </Typography>
                    <Typography variant="caption" color={late ? 'error.main' : 'text.secondary'}>
                      Pedido {formatDay(r.createdAt)}{r.estimatedDelivery ? ` · entrega ${formatDay(r.estimatedDelivery)}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={800} sx={{ whiteSpace: 'nowrap' }}>{formatMoney(r.total)}</Typography>
                </Stack>
                <Button fullWidth variant="contained" color="success" startIcon={<LocalShipping />} sx={{ mt: 1.5, minHeight: 48, fontSize: 16 }}
                  onClick={() => setDelivering(r)}>
                  Marcar entregado
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <DeliverDialog order={delivering} onClose={() => setDelivering(null)} onDone={load} />
    </>
  );
};

const PendingDeliveriesDialog: React.FC<{ open: boolean; onClose: () => void; onChanged?: () => void }> = ({ open, onClose, onChanged }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle>Pedidos a entregar</DialogTitle>
    <DialogContent>{open && <PendingDeliveriesList onChanged={onChanged} />}</DialogContent>
    <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
  </Dialog>
);

export default PendingDeliveriesDialog;
